<template>
  <div class="event-layout">
    <EventSidebar :event-id="eventId" />

    <main class="event-main">
      <div class="mb-6">
        <p class="text-sm font-semibold text-gray-500">Events → {{ eventName }}</p>
        <h1 class="text-3xl tracking-tight font-black text-primary mt-2">Setup</h1>
      </div>

      <div v-if="error" class="notice error">{{ error }}</div>
      <div v-if="successMessage" class="notice success">{{ successMessage }}</div>

      <section class="panel mb-6">
        <h2 class="panel-title">Event Details</h2>
        <div class="grid gap-4 md:grid-cols-3">
          <div>
            <label class="field-label">Event Name</label>
            <input v-model="eventForm.name" type="text" class="field-input" />
          </div>
          <div>
            <label class="field-label">University Slug</label>
            <input v-model="eventForm.universitySlug" type="text" class="field-input" />
          </div>
          <div>
            <label class="field-label">Status</label>
            <select v-model="eventForm.status" class="field-input">
              <option value="draft">Draft</option>
              <option value="active">Active</option>
              <option v-if="canArchiveEvents" value="archived">Archived</option>
            </select>
          </div>
        </div>
        <div class="mt-6">
          <button class="primary-button" @click="saveEventSetup" :disabled="savingEvent">
            {{ savingEvent ? "Saving..." : "Save Event Details" }}
          </button>
        </div>
      </section>

      <section class="panel mb-6">
        <h2 class="panel-title">Student Pronunciation Collection</h2>
        <p class="text-secondary font-medium mt-2">
          Configure whether students can use review links for this event.
        </p>
        <div class="settings-grid mt-5">
          <label class="setting-row">
            <input
              v-model="eventForm.studentPronunciationCollection.enabled"
              type="checkbox"
              :disabled="savingCollectionSettings"
              @change="saveStudentCollectionSettings"
            />
            <span>
              <strong>Enable student review links</strong>
              <small>Students with a secure link can review generated audio and submit a correction until the queue is published.</small>
            </span>
          </label>
          <label
            class="setting-row"
            :class="{ disabled: !eventForm.studentPronunciationCollection.enabled }"
          >
            <input
              v-model="eventForm.studentPronunciationCollection.emailEnabled"
              type="checkbox"
              :disabled="!eventForm.studentPronunciationCollection.enabled || savingCollectionSettings"
              @change="saveStudentCollectionSettings"
            />
            <span>
              <strong>Allow students to request email links</strong>
              <small>Students can enter their email on the public review page and receive their secure link by email.</small>
            </span>
          </label>
        </div>
        <p v-if="collectionSettingsMessage" class="setting-status">
          {{ collectionSettingsMessage }}
        </p>
      </section>

      <section class="panel">
        <div class="flex flex-wrap justify-between gap-4 items-start">
          <div>
            <h2 class="panel-title">CSV Import</h2>
            <p class="text-secondary font-medium mt-2">
              Import participants for this event. Imported rows go to the Participants page, not directly to the live queue.
            </p>
          </div>
          <router-link :to="participantsPath" class="secondary-link">
            Go to Participants
          </router-link>
        </div>

        <div class="mt-6 grid gap-4 md:grid-cols-2">
          <div>
            <label class="field-label">CSV File</label>
            <input type="file" accept=".csv,text/csv" @change="onFileChange" />
          </div>
          <div>
            <label class="field-label">Default Type</label>
            <select v-model="defaultType" class="field-input">
              <option value="student">student</option>
              <option value="presenter_note">presenter_note</option>
              <option value="display_line">display_line</option>
            </select>
          </div>
        </div>

        <div class="mt-6 flex flex-wrap gap-3">
          <button class="secondary-button" @click="analyzeImport" :disabled="!csvText || importLoading">
            {{ importLoading ? "Reading..." : "Read Headers" }}
          </button>
          <button class="primary-button" @click="previewMappedImport" :disabled="!csvText || importLoading">
            Preview Import
          </button>
          <button
            class="primary-button"
            @click="commitPreview"
            :disabled="previewItems.length === 0 || previewErrors.length > 0 || importLoading"
          >
            Import Participants
          </button>
        </div>

        <div v-if="headers.length" class="mt-6 grid gap-4 md:grid-cols-5">
          <div>
            <label class="field-label">Type Column</label>
            <select v-model="mapping.type" class="field-input">
              <option value="">None</option>
              <option v-for="header in headers" :key="`type-${header}`" :value="header">{{ header }}</option>
            </select>
          </div>
          <div>
            <label class="field-label">Full Name Column</label>
            <select v-model="mapping.full_name" class="field-input">
              <option value="">None</option>
              <option v-for="header in headers" :key="`name-${header}`" :value="header">{{ header }}</option>
            </select>
          </div>
          <div>
            <label class="field-label">Award Column</label>
            <select v-model="mapping.award" class="field-input">
              <option value="">None</option>
              <option v-for="header in headers" :key="`award-${header}`" :value="header">{{ header }}</option>
            </select>
          </div>
          <div>
            <label class="field-label">Email Column</label>
            <select v-model="mapping.email" class="field-input">
              <option value="">None</option>
              <option v-for="header in headers" :key="`email-${header}`" :value="header">{{ header }}</option>
            </select>
          </div>
          <div>
            <label class="field-label">Text Column</label>
            <select v-model="mapping.text" class="field-input">
              <option value="">None</option>
              <option v-for="header in headers" :key="`text-${header}`" :value="header">{{ header }}</option>
            </select>
          </div>
        </div>

        <div v-if="previewErrors.length" class="mt-6">
          <h3 class="text-lg font-bold text-primary">Import Errors</h3>
          <ul class="list-disc ml-6 mt-2 text-red-700">
            <li v-for="errorText in previewErrors" :key="errorText">{{ errorText }}</li>
          </ul>
        </div>

        <div v-if="previewItems.length" class="mt-6">
          <h3 class="text-lg font-bold text-primary">Preview</h3>
          <div class="mt-3 space-y-2">
            <div
              v-for="item in previewItems"
              :key="item.id"
              class="preview-row"
            >
              <strong class="capitalize">{{ item.type.replace("_", " ") }}</strong>
              <span v-if="item.type === 'student'">: {{ item.fullName }} / {{ item.award }}</span>
              <span v-else>: {{ item.text }}</span>
            </div>
          </div>
        </div>
      </section>
    </main>
  </div>
</template>

<script>
import { commitImport, fetchEvent, previewImport, saveEvent } from "@/lib/api";
import { hasAnyRole } from "@/lib/auth";
import EventSidebar from "./EventSidebar.vue";

export default {
  name: "EventSetupView",
  components: {
    EventSidebar,
  },
  data() {
    return {
      eventForm: {
        name: "",
        status: "draft",
        universitySlug: "",
        studentPronunciationCollection: {
          enabled: false,
          emailEnabled: false,
        },
      },
      loadedQueueRevision: null,
      csvText: "",
      headers: [],
      mapping: {
        type: "",
        full_name: "",
        award: "",
        email: "",
        text: "",
      },
      defaultType: "student",
      previewItems: [],
      previewErrors: [],
      savingEvent: false,
      savingCollectionSettings: false,
      importLoading: false,
      collectionSettingsMessage: "",
      error: "",
      successMessage: "",
    };
  },
  computed: {
    eventId() {
      return this.$route.params.eventId;
    },
    eventName() {
      return this.eventForm.name || this.eventId;
    },
    participantsPath() {
      return `/events/${encodeURIComponent(this.eventId)}/participants`;
    },
    canArchiveEvents() {
      return hasAnyRole(["internal_admin"]);
    },
  },
  methods: {
    async loadEvent() {
      this.error = "";
      try {
        const snapshot = await fetchEvent(this.eventId);
        this.loadedQueueRevision = snapshot.queue.revision;
        this.eventForm = {
          name: snapshot.event?.name || this.eventId,
          status: snapshot.event?.status || "draft",
          universitySlug: snapshot.event?.universitySlug || "",
          studentPronunciationCollection: this.normalizeStudentPronunciationCollection(
            snapshot.event?.studentPronunciationCollection
          ),
        };
      } catch (error) {
        this.eventForm = {
          name: this.$route.query.name || this.eventId,
          status: "draft",
          universitySlug: "",
          studentPronunciationCollection: this.normalizeStudentPronunciationCollection(),
        };
      }
    },
    normalizeStudentPronunciationCollection(settings = {}) {
      const enabled = Boolean(settings?.enabled);
      return {
        enabled,
        emailEnabled: enabled && Boolean(settings?.emailEnabled),
      };
    },
    async saveEventSetup() {
      this.savingEvent = true;
      this.error = "";
      this.successMessage = "";
      try {
        const payload = {
          ...this.eventForm,
          studentPronunciationCollection: this.normalizeStudentPronunciationCollection(
            this.eventForm.studentPronunciationCollection
          ),
        };
        const saved = await saveEvent(this.eventId, payload);
        if (this.loadedQueueRevision === null) this.loadedQueueRevision = saved.queue.revision;
        this.successMessage = "Event details saved.";
      } catch (error) {
        this.error = error?.response?.data?.error || "Unable to save event details.";
      } finally {
        this.savingEvent = false;
      }
    },
    async saveStudentCollectionSettings() {
      const settings = this.normalizeStudentPronunciationCollection(
        this.eventForm.studentPronunciationCollection
      );
      this.eventForm.studentPronunciationCollection = settings;
      this.savingCollectionSettings = true;
      this.collectionSettingsMessage = "Saving...";
      this.error = "";
      this.successMessage = "";
      try {
        const snapshot = await saveEvent(this.eventId, {
          ...this.eventForm,
          studentPronunciationCollection: settings,
        });
        this.eventForm.studentPronunciationCollection = this.normalizeStudentPronunciationCollection(
          snapshot.event?.studentPronunciationCollection
        );
        this.collectionSettingsMessage = "Saved.";
      } catch (error) {
        this.collectionSettingsMessage = "";
        this.error = error?.response?.data?.error || "Unable to save collection settings.";
        await this.loadEvent();
      } finally {
        this.savingCollectionSettings = false;
      }
    },
    async onFileChange(event) {
      const [file] = event.target.files || [];
      this.previewItems = [];
      this.previewErrors = [];
      this.headers = [];
      if (!file) {
        this.csvText = "";
        return;
      }
      this.csvText = await file.text();
    },
    async analyzeImport() {
      this.importLoading = true;
      this.error = "";
      try {
        const result = await previewImport(this.eventId, {
          csvText: this.csvText,
          mapping: {},
          defaultType: this.defaultType,
        });
        this.headers = result.headers || [];
        this.previewErrors = result.errors || [];
      } catch (error) {
        this.error = error?.response?.data?.error || "Unable to read CSV headers.";
      } finally {
        this.importLoading = false;
      }
    },
    async previewMappedImport() {
      this.importLoading = true;
      this.error = "";
      try {
        const result = await previewImport(this.eventId, {
          csvText: this.csvText,
          mapping: this.mapping,
          defaultType: this.defaultType,
        });
        this.headers = result.headers || [];
        this.previewItems = result.items || [];
        this.previewErrors = result.errors || [];
      } catch (error) {
        this.error = error?.response?.data?.error || "Unable to preview import.";
      } finally {
        this.importLoading = false;
      }
    },
    async commitPreview() {
      this.importLoading = true;
      this.error = "";
      this.successMessage = "";
      try {
        const saved = await commitImport(this.eventId, this.previewItems, this.loadedQueueRevision);
        this.loadedQueueRevision = saved.queue.revision;
        this.successMessage = "Participants imported. Continue in Participants to review and prepare the roster.";
      } catch (error) {
        this.error = error?.response?.data?.error || "Unable to import participants.";
      } finally {
        this.importLoading = false;
      }
    },
  },
  mounted() {
    this.loadEvent();
  },
};
</script>

<style scoped>
.event-layout {
  display: flex;
  gap: 1rem;
  width: calc(100vw - 0.25rem);
  max-width: none;
  box-sizing: border-box;
  margin: 0 auto;
  padding: 0 0.125rem 2.5rem;
}

.event-main {
  flex: 1 1 auto;
  min-width: 0;
}

:deep(.sidebar) {
  width: 168px;
  flex-basis: 168px;
}

.panel {
  background: white;
  border: 1px solid var(--orator-smoke);
  border-radius: 0.75rem;
  box-shadow: 0 1px 3px 0 rgb(0 0 0 / 0.06);
  padding: 1.5rem;
}

.panel-title {
  font-size: 1.5rem;
  font-weight: 900;
  color: var(--orator-navy);
}

.field-label {
  display: block;
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--orator-arsenic);
  margin-bottom: 0.5rem;
}

.field-input {
  display: block;
  width: 100%;
  border-radius: 0.375rem;
  border: 1px solid var(--orator-steel);
  box-shadow: 0 1px 2px 0 rgb(0 0 0 / 0.05);
  padding: 0.625rem 0.75rem;
}

.settings-grid {
  display: grid;
  gap: 0.8rem;
}

.setting-row {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  border: 1px solid var(--orator-smoke);
  border-radius: 0.5rem;
  padding: 0.95rem;
}

.setting-row input {
  margin-top: 0.2rem;
}

.setting-row strong,
.setting-row small {
  display: block;
}

.setting-row strong {
  color: var(--orator-navy);
  font-weight: 850;
}

.setting-row small {
  margin-top: 0.25rem;
  color: var(--orator-graphite);
  line-height: 1.45;
}

.setting-row.disabled {
  opacity: 0.62;
}

.setting-status {
  margin-top: 0.8rem;
  color: var(--orator-graphite);
  font-size: 0.86rem;
  font-weight: 700;
}

.primary-button,
.secondary-button,
.secondary-link {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.625rem 0.95rem;
  border-radius: 0.375rem;
  font-weight: 700;
  text-decoration: none;
  border: 1px solid var(--orator-steel);
}

.primary-button {
  background: var(--orator-blue);
  border-color: var(--orator-blue);
  color: white;
}

.secondary-button,
.secondary-link {
  background: white;
  color: var(--orator-arsenic);
}

.preview-row {
  background: white;
  border: 1px solid var(--orator-smoke);
  border-radius: 0.5rem;
  padding: 0.75rem 1rem;
}

.notice {
  margin-bottom: 1rem;
  padding: 0.9rem 1rem;
  border-radius: 0.65rem;
  font-weight: 600;
}

.notice.error {
  color: #b91c1c;
  background: #fef2f2;
  border: 1px solid #fecaca;
}

.notice.success {
  color: #166534;
  background: #f0fdf4;
  border: 1px solid #bbf7d0;
}

@media (max-width: 960px) {
  .event-layout {
    width: auto;
    flex-direction: column;
    padding: 0 1rem 2.5rem;
  }

  :deep(.sidebar) {
    width: 100%;
    flex-basis: 100%;
  }
}
</style>
